import type {
  CandidatoDeCierre,
  EstatusDeCierre,
} from '@/features/rh/api/bandejaDeCierre'
import { formatIsoEnZona } from '@/shared/lib/dateUtils'

/**
 * Reglas de la bandeja de cierre, fuera de la pantalla para poder probarlas.
 */

export interface PestanaDeCierre {
  id: EstatusDeCierre
  titulo: string
}

export const PESTANAS: readonly PestanaDeCierre[] = [
  { id: 'pendiente_cierre', titulo: 'Por cerrar' },
  { id: 'pendiente_direccion', titulo: 'En validación de Dirección' },
  // Dirección ya no cierra (SRH-311): decide y RH confirma desde aquí.
  { id: 'decision_direccion', titulo: 'Decisión de Dirección' },
  { id: 'seleccionado', titulo: 'Seleccionados' },
] as const

/**
 * Las decisiones de RH en el cierre.
 *
 * Descartar solo existe para confirmar lo que Dirección rechazó: el solicitante
 * ya aprobó, así que RH no rechaza por su cuenta. Devolver al solicitante se
 * quitó: RH termina de seleccionar o manda a Dirección.
 */
export type AccionDeCierre = 'seleccionar' | 'enviar_direccion' | 'descartar'

export const ACCIONES: ReadonlyArray<{
  id: AccionDeCierre
  etiqueta: string
  /** Para la fila, donde los botones de texto largos no caben. */
  corta: string
  /** Lo que se le pregunta antes de ejecutarla. */
  confirmacion: string
}> = [
  {
    id: 'seleccionar',
    corta: 'Seleccionar',
    etiqueta: 'Seleccionar',
    confirmacion:
      '¿Seleccionar a este candidato? Se le avisa por correo y el proceso queda cerrado.',
  },
  {
    id: 'enviar_direccion',
    corta: 'A Dirección',
    etiqueta: 'Enviar a Dirección',
    confirmacion:
      '¿Mandar este candidato a validación de Dirección? Dirección decide si lo selecciona o lo descarta.',
  },
  {
    id: 'descartar',
    corta: 'Descartar',
    etiqueta: 'Descartar',
    confirmacion: '¿Descartar a este candidato?',
  },
] as const

/**
 * Solo se decide sobre lo que está esperando el cierre de RH: lo que acaba de
 * llegar y lo que Dirección ya decidió. En «En validación de Dirección» decide
 * Dirección, y en «Seleccionados» ya se cerró.
 */
export const sePuedeDecidir = (status: EstatusDeCierre): boolean =>
  status === 'pendiente_cierre' || status === 'decision_direccion'

/**
 * Qué puede hacer RH con cada candidato.
 *
 * Lo que el solicitante ya aprobó RH lo termina de seleccionar o lo manda a
 * Dirección: no lo rechaza ni lo devuelve, porque la decisión de rechazo
 * no es suya. Con la decisión de Dirección recibida (SRH-311) RH la confirma:
 * selecciona si aprobó, descarta si rechazó, y nada más.
 *
 * Esto solo evita ofrecer botones que ya no aplican; el servidor no cambió.
 */
export const accionesDeCierre = (
  status: EstatusDeCierre,
  direccion?: CandidatoDeCierre['directionDecision'],
): AccionDeCierre[] => {
  if (status === 'pendiente_cierre') {
    return ['seleccionar', 'enviar_direccion']
  }
  if (status === 'decision_direccion') {
    if (direccion?.result === 'approved') return ['seleccionar']
    if (direccion?.result === 'rejected') return ['descartar']
  }
  return []
}

/** `Aprobó · Diana Directora · 2026-10-22 18:00`. */
export const textoDeDecisionDeDireccion = (
  decision: CandidatoDeCierre['directionDecision'],
): string => {
  if (!decision) return '------'
  const que = decision.result === 'approved' ? 'Aprobó' : 'Rechazó'
  return [que, decision.userName, formatIsoEnZona(decision.at)]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Lo que se precarga al descartar lo que Dirección rechazó: su motivo y su
 * comentario. RH los puede cambiar; empezar de cero es lo que se pidió evitar.
 */
export const precargaDeDescarte = (
  decision: CandidatoDeCierre['directionDecision'],
): { motivo: string; comentario: string } =>
  decision?.result === 'rejected'
    ? { motivo: decision.reason ?? '', comentario: decision.comment ?? '' }
    : { motivo: '', comentario: '' }

/**
 * Los motivos que se ofrecen. Si el que trae Dirección no está en el catálogo se
 * suma al principio, porque un select sin esa opción dejaría el campo en blanco
 * justo cuando se precargó.
 */
export const opcionesDeMotivo = (
  catalogo: readonly string[],
  precargado: string,
): string[] =>
  precargado && !catalogo.includes(precargado)
    ? [precargado, ...catalogo]
    : [...catalogo]

/**
 * La pestaña a la que se va el candidato después de una acción.
 *
 * Descartar lo saca de la bandeja: el descartado vive en la pestaña de
 * Descartados del tablero.
 */
export const pestanaDestino = (
  accion: AccionDeCierre,
): EstatusDeCierre | null => {
  switch (accion) {
    case 'seleccionar':
      return 'seleccionado'
    case 'enviar_direccion':
      return 'pendiente_direccion'
    default:
      return null
  }
}

/** `Sara Solicitante · 2026-10-14 18:00`, o un guion si no hay aprobación. */
export const textoDeAprobacion = (
  aprobacion: CandidatoDeCierre['requesterApproval'],
): string =>
  aprobacion
    ? `${aprobacion.userName} · ${formatIsoEnZona(aprobacion.at)}`
    : '------'

/**
 * Quién cerró el proceso y cuándo.
 *
 * `role` llega nulo en los que ya estaban seleccionados antes de RH-03.1,
 * cuando nadie registraba quién decidía; ahí se muestra solo el nombre.
 */
export const textoDeDecisionFinal = (
  decision: CandidatoDeCierre['finalDecision'],
): string => {
  if (!decision) return '------'

  const quien =
    decision.role === 'rh'
      ? 'RH'
      : decision.role === 'direction'
        ? 'Dirección'
        : null

  const nombre = quien ? `${decision.userName} (${quien})` : decision.userName
  return `${nombre} · ${formatIsoEnZona(decision.at)}`
}

/** Mensaje del error, con el caso del 409 dicho en claro. */
export const mensajeDeFallo = (error: unknown): string => {
  const e = error as { statusCode?: number; status?: number; message?: string }
  const codigo = e?.statusCode ?? e?.status

  if (codigo === 409) {
    // El conflicto con lo que decidió Dirección trae su propia explicación y es
    // más útil que el genérico: dice qué sí se puede hacer.
    if (e.message?.includes('Dirección')) return e.message
    return 'Ya se tomó una decisión sobre este candidato. Se actualizó la lista.'
  }

  return e?.message || 'No se pudo completar la acción. Intenta de nuevo.'
}
